import React, { useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import ActionButton from "../components/ActionButton";
import ComplianceEvidencePack from "../components/ComplianceEvidencePack";
import { formatDateTime } from "../utils/datetime";

type Framework = { key: string; label: string; control_count: number };
type Attestation = {
  id: number;
  customer_id: number | null;
  framework_key: string;
  signatory_name: string;
  signatory_title: string | null;
  signatory_email: string | null;
  statement: string | null;
  period_label: string | null;
  readiness_pct_at_attestation: number | null;
  status: string;
  attested_at: string;
  attested_by_username: string | null;
};
type AssessmentSummary = {
  action_id: number;
  framework: string | null;
  framework_key: string | null;
  as_of: string | null;
  overall_readiness_pct: number | null;
  summary: string | null;
  customer_id: number | null;
  created_at: string | null;
  model_used: string | null;
};
type ControlRow = {
  id: string;
  name: string;
  status: "met" | "partial" | "gap" | "unknown" | string;
  evidence: string;
  next_step: string;
};
type AssessmentDetail = {
  action_id: number;
  framework: string | null;
  framework_key: string | null;
  as_of: string | null;
  overall_readiness_pct: number | null;
  summary: string | null;
  controls: ControlRow[];
  posture: Record<string, any> | null;
  customer_id: number | null;
  model_used: string | null;
  created_at: string | null;
};
type Customer = { id: number; name: string };

const STATUS_BADGE: Record<string, string> = {
  met: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800",
  partial: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800",
  gap: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800",
  unknown: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_BADGE[status?.toLowerCase()] || STATUS_BADGE.unknown;
  return (
    <span className={`inline-flex items-center text-xs font-semibold border px-2 py-0.5 rounded-full ${cls}`}>
      {(status || "unknown").toUpperCase()}
    </span>
  );
}

function ReadinessPill({ pct }: { pct: number | null | undefined }) {
  if (pct == null) return <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>;
  let cls = "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
  if (pct >= 80) cls = "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
  else if (pct >= 50) cls = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
  return (
    <span className={`inline-flex items-center text-sm font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {pct}%
    </span>
  );
}

export default function AICompliance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [framework, setFramework] = useState<string>("hipaa");
  const { currentCustomerId } = useCustomer();
  const [customerId, setCustomerId] = useState<number | "">(currentCustomerId ?? "");
  const [instruction, setInstruction] = useState("");
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [detail, setDetail] = useState<AssessmentDetail | null>(null);
  const [running, setRunning] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [err, setErr] = useState("");

  // Org attestation for the currently-selected framework + scope.
  const [attestation, setAttestation] = useState<Attestation | null>(null);
  const [attForm, setAttForm] = useState({
    signatory_name: "",
    signatory_title: "",
    signatory_email: "",
    statement: "",
    period_label: "",
  });
  const [attBusy, setAttBusy] = useState(false);
  const [attMsg, setAttMsg] = useState("");

  const loadAttestation = async () => {
    try {
      const params: any = { framework };
      if (customerId !== "") params.customer_id = Number(customerId);
      const r = await api.get("/api/v1/compliance/attestations", { params });
      const active = (r.data || []).find((a: Attestation) => a.status === "active") || null;
      setAttestation(active);
    } catch {
      setAttestation(null);
    }
  };

  const recordAttestation = async () => {
    if (!attForm.signatory_name.trim()) {
      setAttMsg("Signatory name is required.");
      return;
    }
    setAttBusy(true);
    setAttMsg("");
    try {
      await api.post("/api/v1/compliance/attestations", {
        framework,
        customer_id: customerId === "" ? null : Number(customerId),
        signatory_name: attForm.signatory_name.trim(),
        signatory_title: attForm.signatory_title.trim() || null,
        signatory_email: attForm.signatory_email.trim() || null,
        statement: attForm.statement.trim() || null,
        period_label: attForm.period_label.trim() || null,
      });
      setAttForm({ signatory_name: "", signatory_title: "", signatory_email: "", statement: "", period_label: "" });
      setAttMsg("Attestation recorded.");
      await loadAttestation();
    } catch (e: any) {
      setAttMsg(e.response?.data?.detail || "Failed to record attestation");
    } finally {
      setAttBusy(false);
    }
  };

  const revokeAttestation = async () => {
    if (!attestation) return;
    if (!window.confirm("Revoke the current attestation? It will no longer appear on reports.")) return;
    setAttBusy(true);
    try {
      await api.post(`/api/v1/compliance/attestations/${attestation.id}/revoke`);
      await loadAttestation();
    } catch (e: any) {
      setAttMsg(e.response?.data?.detail || "Failed to revoke");
    } finally {
      setAttBusy(false);
    }
  };

  const loadFrameworks = async () => {
    try {
      const r = await api.get("/api/v1/ai/compliance/frameworks");
      setFrameworks(r.data.frameworks || []);
    } catch {
      /* ignore */
    }
  };
  const loadCustomers = async () => {
    try {
      const r = await api.get("/api/v1/customers");
      const rows: Customer[] = (r.data || []).map((c: any) => ({ id: c.id, name: c.name }));
      setCustomers(rows);
    } catch {
      /* ignore — customer dropdown is optional */
    }
  };
  const loadAssessments = async () => {
    try {
      const r = await api.get("/api/v1/ai/compliance/assessments", { params: { limit: 50 } });
      setAssessments(r.data.assessments || []);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    loadFrameworks();
    loadCustomers();
    loadAssessments();
  }, []);

  // The global customer switcher drives this page's customer scope.
  useEffect(() => {
    setCustomerId(currentCustomerId ?? "");
  }, [currentCustomerId]);

  useEffect(() => {
    loadAttestation(); /* eslint-disable-next-line */
  }, [framework, customerId]);

  /**
   * Fetch a framework report with the session attached.
   *
   * These were plain <a href> links, which send no Authorization header — the
   * API is bearer-token authenticated, not cookie authenticated, so clicking
   * one returned {"detail":"Not authenticated"} and the report was simply
   * unreachable from the UI.
   */
  /** Has THIS framework been assessed for THIS scope yet?
   *
   *  GATES THE PDF AND CSV DOWNLOADS. Asked for three times, so it is the
   *  decision, and the reasoning against it is recorded here rather than
   *  relitigated:
   *
   *  The framework report is DETERMINISTIC - rule-based off the live
   *  posture, the same input producing the same document - so the
   *  assessment is not technically a prerequisite, and requiring one
   *  spends a paid AI call to unlock a document that was already correct.
   *
   *  The governance argument wins anyway: this is the artefact somebody
   *  hands an auditor, and "nobody has looked at this environment yet" is
   *  a real reason not to produce one. Making the assessment the first
   *  step also means the dated snapshot exists, so a later report has
   *  something to be compared against.
   *
   *  Matched on framework AND scope, never framework alone: an assessment
   *  of a DIFFERENT customer says nothing about this one, and "we assessed
   *  somebody" is exactly the near-miss that reads as a pass.
   */
  const assessedThisScope = assessments.some(
    (a) =>
      (a.framework_key || "") === framework &&
      (a.customer_id ?? null) === (customerId === "" ? null : Number(customerId)),
  );

  /** The framework the DOCUMENT will describe.
   *
   *  Derived, not read straight off the select. Syncing the select when
   *  an assessment is opened fixes the reported case, but leaving the
   *  download to read a separate piece of state keeps the same class of
   *  bug one careless change away. When a detail pane is open, the
   *  document follows what is ON SCREEN.
   */
  const reportFramework = detail?.framework_key || framework;
  const reportScope =
    detail && detail.customer_id !== undefined
      ? (detail.customer_id ?? "")
      : customerId;

  /** Draft POA&M for the framework on screen.
   *
   *  Uses reportFramework / reportScope, the same derived values the PDF
   *  uses, NOT the raw select. A POA&M for the wrong framework is the
   *  same defect as a FERPA report titled HIPAA, and deriving both from
   *  one place is what stops it recurring.
   */
  const downloadPoam = async () => {
    setErr("");
    try {
      await downloadFile(
        "/api/v1/compliance/poam.csv",
        `${reportFramework}-poam-draft.csv`,
        {
          framework: reportFramework,
          ...(reportScope !== "" ? { customer_id: Number(reportScope) } : {}),
        },
      );
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  /** Named machines and accounts behind every finding.
   *
   *  Deliberately NOT framework-scoped — the assets are the same whichever
   *  framework you are assessed against, so scoping it by framework would
   *  imply a difference that does not exist.
   */
  const downloadAffectedAssets = async () => {
    setErr("");
    try {
      await downloadFile(
        "/api/v1/compliance/affected-assets.csv",
        "affected-assets.csv",
        reportScope !== "" ? { customer_id: Number(reportScope) } : {},
      );
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const downloadReport = async (fmt: "pdf" | "csv") => {
    setErr("");
    try {
      await downloadFile(
        `/api/v1/compliance/framework-report.${fmt}`,
        `${reportFramework}-framework-report.${fmt}`,
        {
          framework: reportFramework,
          ...(reportScope !== "" ? { customer_id: Number(reportScope) } : {}),
        },
      );
    } catch (e: any) {
      // A failed download used to be a blank tab with a JSON error in it.
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "That report could not be generated.",
      );
    }
  };

  const run = async () => {
    setErr("");
    setRunning(true);
    try {
      const r = await api.post("/api/v1/ai/compliance/assess", {
        framework,
        customer_id: customerId === "" ? null : Number(customerId),
        instruction: instruction.trim() || null,
      });
      // The assess endpoint already returns full detail; render it immediately.
      setDetail({
        action_id: r.data.action_id,
        framework: r.data.framework,
        framework_key: r.data.framework_key,
        as_of: r.data.as_of,
        overall_readiness_pct: r.data.overall_readiness_pct,
        summary: r.data.summary,
        controls: r.data.controls || [],
        posture: r.data.posture || null,
        customer_id: customerId === "" ? null : Number(customerId),
        model_used: null,
        created_at: r.data.as_of,
      });
      await loadAssessments();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  const openAssessment = async (id: number) => {
    setErr("");
    setLoadingDetail(true);
    try {
      const r = await api.get(`/api/v1/ai/compliance/assessments/${id}`);
      setDetail(r.data);
      // REPORTED: "When I ran the ferpa report for NCST the PDF says HIPAA
      // those are different things."
      //
      // They are, and this is where they diverged. Opening an assessment
      // filled the detail pane but left the framework SELECT on whatever
      // it was - "hipaa", its initial value - and the download button
      // reads the select. So: look at a FERPA assessment, click Download
      // framework report, receive a HIPAA document. In a compliance
      // artefact that is not a cosmetic mislabel, it is the wrong
      // regulation entirely.
      //
      // Opening an assessment now moves the whole page to that
      // framework, so the controls on screen and the document you can
      // download describe the same thing.
      if (r.data?.framework_key) setFramework(r.data.framework_key);
      if (r.data?.customer_id !== undefined) {
        setCustomerId(r.data.customer_id ?? "");
      }
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  const customerNameById = useMemo(() => {
    const m = new Map<number, string>();
    customers.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [customers]);

  const controlsCounted = useMemo(() => {
    if (!detail?.controls) return { met: 0, partial: 0, gap: 0, unknown: 0, total: 0 };
    let met = 0, partial = 0, gap = 0, unknown = 0;
    detail.controls.forEach((c) => {
      const s = (c.status || "").toLowerCase();
      if (s === "met") met++;
      else if (s === "partial") partial++;
      else if (s === "gap") gap++;
      else unknown++;
    });
    return { met, partial, gap, unknown, total: detail.controls.length };
  }, [detail]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Compliance</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Score the current RMM posture against a compliance framework. Results are AI-generated
          starting points — use them to focus your audit prep, not as a substitute for one.
        </p>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <h2 className="font-medium text-gray-800 dark:text-gray-100 mb-3">Run a new assessment</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-sm">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Framework</span>
            <select
              value={framework}
              onChange={(e) => setFramework(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              {frameworks.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label} ({f.control_count} controls)
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Customer scope</span>
            <span className="inline-block border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900">
              {customerId === ""
                ? "MSP-wide (all customers)"
                : customerNameById.get(Number(customerId)) || `Customer #${customerId}`}
            </span>
            <span className="block text-xs text-gray-400 mt-1">Set via the header switcher.</span>
          </div>
          <label className="text-sm md:col-span-1">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Focus (optional)</span>
            <input
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="e.g. focus on PHI handling"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button
            onClick={run}
            disabled={running}
            className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {running ? "Assessing…" : "Run assessment"}
          </button>
          {/* Fetched through the api client, NOT a plain <a href>. The session
              is a bearer token in a header, not a cookie, so a bare link
              arrives unauthenticated — which is exactly what happened: clicking
              this returned {"detail":"Not authenticated"}. downloadFile also
              takes the filename from Content-Disposition, so the saved file is
              named the way the server intended rather than after the URL. */}
          <ActionButton
            onAction={() => downloadReport("pdf")}
            variant="raw"
            disabled={!assessedThisScope}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            pendingLabel="Building…"
            title={
              assessedThisScope
                ? "Deterministic and rule-based — no AI. The same posture always produces the same document. Hand directly to an auditor."
                : "Run the assessment for this framework and customer first."
            }
          >
            Download framework report (PDF)
          </ActionButton>
          <ActionButton
            onAction={() => downloadReport("csv")}
            variant="raw"
            disabled={!assessedThisScope}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            pendingLabel="Building…"
            title={
              assessedThisScope
                ? "The same control table as the PDF, as a spreadsheet."
                : "Run the assessment for this framework and customer first."
            }
          >
            CSV
          </ActionButton>
          {/* The two exports that turn this from a scorecard into working
              documents. Ungated, unlike the framework report: a POA&M and
              an asset list are internal remediation tools, not the
              artefact handed to an auditor, so withholding them until an
              assessment has run only delays the work. */}
          <ActionButton
            onAction={downloadPoam}
            variant="raw"
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900"
            pendingLabel="Building…"
            title="Draft Plan of Action and Milestones for this framework's gaps, in priority order. Owner and target date are left blank for you to commit to."
          >
            POA&amp;M (draft)
          </ActionButton>
          <ActionButton
            onAction={downloadAffectedAssets}
            variant="raw"
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900"
            pendingLabel="Building…"
            title="Every finding with the machines and accounts behind it, by name, uncapped. Not framework-specific — the assets are the same whichever framework you are assessed against."
          >
            Affected assets
          </ActionButton>
          {/* ASKED three times, most recently: "and I still do not have to
              run the assessment I can just click on PDF or csv to get the
              data."

              The gate is back. The counter-argument is recorded on
              assessedThisScope above rather than repeated here: the report
              is deterministic so the assessment is not technically needed,
              but this is the artefact that goes to an auditor and
              "nobody has looked yet" is a real reason to withhold it.

              The reason lives NEXT TO the disabled button, not only in a
              tooltip - a greyed control with its explanation hidden behind
              a hover is a dead end on a touch screen and reads as broken
              rather than sequenced. */}
          <span className="text-xs text-gray-500 dark:text-gray-400 max-w-xl">
            Two documents from the same posture.{" "}
            <strong className="text-gray-700 dark:text-gray-200">
              The framework report
            </strong>{" "}
            is rule-based and deterministic — the same posture always
            produces the same document, and it is the one you hand an
            auditor.{" "}
            <strong className="text-gray-700 dark:text-gray-200">
              Running an assessment
            </strong>{" "}
            adds an AI reading of those same numbers and records a dated
            snapshot.{" "}
            {assessedThisScope ? (
              <span className="text-green-700 dark:text-green-400">
                Assessed for this framework and customer — the report is
                available.
              </span>
            ) : (
              <span className="text-amber-700 dark:text-amber-400">
                Run the assessment for this framework and customer first:
                the report is what goes to an auditor, so it is not produced
                before anyone has looked.
              </span>
            )}
          </span>
        </div>
      </div>

      <ComplianceEvidencePack
        customerId={customerId}
        customerName={
          customerId === "" ? null : customerNameById.get(Number(customerId)) || null
        }
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-gray-800 dark:text-gray-100">
            Organization attestation
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {framework.toUpperCase()} ·{" "}
              {customerId === ""
                ? "MSP-wide"
                : customerNameById.get(Number(customerId)) || `Customer #${customerId}`}
            </span>
          </h2>
        </div>

        {attestation ? (
          <div className="mt-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-gray-800 dark:text-gray-100">
                <div className="font-semibold">
                  Attested by {attestation.signatory_name}
                  {attestation.signatory_title && `, ${attestation.signatory_title}`}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                  {formatDateTime(attestation.attested_at)}
                  {attestation.period_label && ` · ${attestation.period_label}`}
                  {attestation.readiness_pct_at_attestation != null &&
                    ` · readiness at sign-off ${attestation.readiness_pct_at_attestation}%`}
                  {attestation.attested_by_username && ` · recorded by ${attestation.attested_by_username}`}
                </div>
                {attestation.statement && (
                  <p className="text-xs italic text-gray-600 dark:text-gray-300 mt-1">
                    “{attestation.statement}”
                  </p>
                )}
              </div>
              {isAdmin && (
                <button
                  onClick={revokeAttestation}
                  disabled={attBusy}
                  className="text-xs text-red-600 dark:text-red-400 hover:underline shrink-0"
                >
                  Revoke
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No active attestation on file for this framework and scope. Once the
            responsible officer has reviewed the posture, record their sign-off —
            it appears on the framework report PDF.
          </p>
        )}

        {isAdmin && (
          <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                value={attForm.signatory_name}
                onChange={(e) => setAttForm({ ...attForm, signatory_name: e.target.value })}
                placeholder="Signatory name *"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <input
                value={attForm.signatory_title}
                onChange={(e) => setAttForm({ ...attForm, signatory_title: e.target.value })}
                placeholder="Title (e.g. CISO)"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <input
                value={attForm.signatory_email}
                onChange={(e) => setAttForm({ ...attForm, signatory_email: e.target.value })}
                placeholder="Email"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <input
                value={attForm.period_label}
                onChange={(e) => setAttForm({ ...attForm, period_label: e.target.value })}
                placeholder="Period (e.g. 2026 Q2)"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <input
                value={attForm.statement}
                onChange={(e) => setAttForm({ ...attForm, statement: e.target.value })}
                placeholder="Statement (optional)"
                className="md:col-span-2 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={recordAttestation}
                disabled={attBusy}
                className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {attBusy ? "Saving…" : attestation ? "Re-attest (supersede)" : "Record attestation"}
              </button>
              {attMsg && <span className="text-xs text-gray-500 dark:text-gray-400">{attMsg}</span>}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-medium text-gray-800 dark:text-gray-100">
            Recent assessments
          </div>
          {assessments.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
              None yet — run your first assessment to populate this list.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[520px] overflow-y-auto">
              {assessments.map((a) => (
                <li
                  key={a.action_id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 ${
                    detail?.action_id === a.action_id ? "bg-blue-50 dark:bg-blue-900/20" : ""
                  }`}
                  onClick={() => openAssessment(a.action_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate pr-2">
                      {a.framework || a.framework_key || "Unknown"}
                    </div>
                    <ReadinessPill pct={a.overall_readiness_pct} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {a.customer_id ? customerNameById.get(a.customer_id) || `Customer #${a.customer_id}` : "MSP-wide"}
                    {a.created_at && ` · ${formatDateTime(a.created_at)}`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          {loadingDetail && (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
          )}
          {!loadingDetail && !detail && (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              Run an assessment or pick one from the list to see control-by-control scoring.
            </div>
          )}
          {detail && (
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Assessment #{detail.action_id}
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
                    {detail.framework}
                  </h2>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {detail.customer_id ? `Scope: ${customerNameById.get(detail.customer_id) || `Customer #${detail.customer_id}`}` : "Scope: MSP-wide"}
                    {detail.created_at && ` · As of ${formatDateTime(detail.created_at)}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Overall readiness</div>
                  <ReadinessPill pct={detail.overall_readiness_pct} />
                </div>
              </div>

              {detail.summary && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                  {detail.summary}
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
                <span className="px-2 py-1 rounded bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300">Met: {controlsCounted.met}</span>
                <span className="px-2 py-1 rounded bg-yellow-50 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300">Partial: {controlsCounted.partial}</span>
                <span className="px-2 py-1 rounded bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300">Gap: {controlsCounted.gap}</span>
                <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">Unknown: {controlsCounted.unknown}</span>
                <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Total: {controlsCounted.total}</span>
              </div>

              {detail.posture && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(detail.posture).filter(([k]) => k !== "as_of" && k !== "scope" && k !== "customer_id").map(([k, v]) => (
                    <div key={k} className="bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded p-2">
                      <div className="text-gray-500 dark:text-gray-400">{k.replace(/_/g, " ")}</div>
                      <div className="font-medium text-gray-800 dark:text-gray-100">{String(v)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">Controls</div>
                {detail.controls.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    No controls returned. The AI may have refused to produce structured output; rerun the assessment.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                    {detail.controls.map((c, i) => (
                      <div key={`${c.id}-${i}`} className="border border-gray-100 dark:border-gray-700 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                              <span className="text-gray-500 dark:text-gray-400 font-mono mr-2">{c.id}</span>
                              {c.name}
                            </div>
                          </div>
                          <StatusBadge status={c.status} />
                        </div>
                        {c.evidence && (
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1.5">
                            <span className="font-medium">Evidence:</span> {c.evidence}
                          </div>
                        )}
                        {c.next_step && (
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            <span className="font-medium">Next step:</span> {c.next_step}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
